import React from "react";
import { useNavigate } from "react-router-dom";
import nammaLogo from "../Images/Namma/NammaLogo.png";
import { SvgCityscape, SvgMeshGrid } from "./SvgDecorations";

const LINKS = {
  Product: [
    { label: "Namma Pass", path: "/nammapass" },
    { label: "Features",   path: "/nammapass#what" },
    { label: "App Experience", path: "/nammapass#experience" },
    { label: "Bengaluru Namma Metro Map", path: "https://bengalurumetro.in/metro-map.html", external: true },
  ],
  Company: [
    { label: "Blog",       path: "/blog" },
    { label: "About Reker", path: "https://reker.world", external: true },
    { label: "LinkedIn",   path: "https://www.linkedin.com/company/reker-world", external: true },
  ],
  Legal: [
    { label: "Privacy Policy",    path: "https://reker.world/privacy-policy", external: true },
    { label: "Terms of Service",  path: "https://reker.world/terms-and-conditions", external: true },
  ],
};

const SOCIALS = [
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/reker-world",
    icon: (
      <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
      </svg>
    ),
  },
  {
    label: "Twitter / X",
    href: "https://twitter.com/rekerworld",
    icon: (
      <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.253 5.622 5.911-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
      </svg>
    ),
  },
  {
    label: "Instagram",
    href: "https://instagram.com/rekerworld",
    icon: (
      <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
      </svg>
    ),
  },
];

const Footer: React.FC = () => {
  const navigate = useNavigate();

  const handleNav = (path: string, external?: boolean) => {
    if (external) { window.open(path, "_blank"); return; }
    if (path.includes("#")) {
      const [route, hash] = path.split("#");
      navigate(route);
      setTimeout(() => document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" }), 100);
    } else {
      navigate(path);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <footer className="np-footer">
      {/* cityscape SVG above footer */}
      <div style={{ position: "relative", overflow: "hidden", lineHeight: 0 }}>
        <SvgCityscape />
      </div>
      {/* mesh grid */}
      <SvgMeshGrid opacity={0.025} />
      {/* top gradient line */}
      <div className="np-footer-topline" />

      <div className="np-footer-inner">

        {/* brand col */}
        <div className="np-footer-brand">
          <img src={nammaLogo} alt="Namma Pass" className="np-footer-logo" loading="lazy" />
          <p className="np-footer-tagline">
            Bengaluru's all-digital metro membership.<br />
            Tap. Ride. Earn.
          </p>
          <div className="np-footer-stores">
            <a href="https://play.google.com/store/apps/details?id=reker.example.evoltech&pcampaignid=web_share" target="_blank" rel="noopener noreferrer" className="np-footer-store-badge" aria-label="Get it on Google Play">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M3.18 1.07C2.76 1.3 2.5 1.76 2.5 2.37v19.26c0 .61.26 1.07.68 1.3l.1.06 10.79-10.79v-.25L3.28 1.01l-.1.06z" fill="url(#fp-a)"/>
                <path d="M17.57 15.4l-3.6-3.6v-.26l3.6-3.6.08.05 4.27 2.43c1.22.69 1.22 1.82 0 2.51l-4.27 2.43-.08.04z" fill="url(#fp-b)"/>
                <path d="M17.65 15.36L13.97 11.67 3.18 22.46c.4.43 1.06.48 1.8.05l12.67-7.15" fill="url(#fp-c)"/>
                <path d="M17.65 8.64L4.98 1.49C4.24 1.06 3.58 1.11 3.18 1.54l10.79 10.13 3.68-3.03z" fill="url(#fp-d)"/>
                <defs>
                  <linearGradient id="fp-a" x1="13.17" y1="2.1" x2="-1.17" y2="16.44" gradientUnits="userSpaceOnUse"><stop stopColor="#00A0FF"/><stop offset="1" stopColor="#00BEFF"/></linearGradient>
                  <linearGradient id="fp-b" x1="22.5" y1="11.67" x2="2.18" y2="11.67" gradientUnits="userSpaceOnUse"><stop stopColor="#FFD000"/><stop offset="1" stopColor="#FFBC00"/></linearGradient>
                  <linearGradient id="fp-c" x1="15.17" y1="13.37" x2="-3.5" y2="32.04" gradientUnits="userSpaceOnUse"><stop stopColor="#FF3A44"/><stop offset="1" stopColor="#C31162"/></linearGradient>
                  <linearGradient id="fp-d" x1="1.07" y1="-3.8" x2="9.47" y2="4.6" gradientUnits="userSpaceOnUse"><stop stopColor="#32A071"/><stop offset="1" stopColor="#2DA771"/></linearGradient>
                </defs>
              </svg>
              <div className="np-footer-store-badge-text">
                <span className="np-footer-store-badge-label">Get it on</span>
                <span className="np-footer-store-badge-name">Google Play</span>
              </div>
            </a>
            <a href="https://apps.apple.com/app/reker/id6504998440" target="_blank" rel="noopener noreferrer" className="np-footer-store-badge" aria-label="Download on App Store">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
              <div className="np-footer-store-badge-text">
                <span className="np-footer-store-badge-label">Download on the</span>
                <span className="np-footer-store-badge-name">App Store</span>
              </div>
            </a>
          </div>
          <div className="np-footer-socials">
            {SOCIALS.map(s => (
              <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer"
                className="np-footer-social" aria-label={s.label}>
                {s.icon}
              </a>
            ))}
          </div>
        </div>

        {/* link cols */}
        {Object.entries(LINKS).map(([group, items]) => (
          <div key={group} className="np-footer-col">
            <h4 className="np-footer-col-title">{group}</h4>
            <ul className="np-footer-col-list">
              {items.map(item => (
                <li key={item.label}>
                  <button className="np-footer-link"
                    onClick={() => handleNav(item.path, (item as any).external)}>
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}

      </div>

      {/* bottom bar */}
      <div className="np-footer-bottom">
        <span>© {new Date().getFullYear()} Evolvevol Tech Pvt. Ltd. All rights reserved.</span>
        <span className="np-footer-bottom-right">
          Namma Pass for <span style={{ color: "#8cff2e" }}>ಹಸಿರು ಬೆಂಗಳೂರು</span>
        </span>
      </div>
    </footer>
  );
};

export default Footer;
